import React from 'react';
import PropTypes from 'prop-types';

import { Box, Button, Container, Grid, Typography } from '@mui/joy';

import i18n from '@/i18n'

import { Loading } from '@/components/Loading';
import { useParams } from 'react-router-dom';

import { StatisticsContainer } from '../../statistics';

import { HBox } from '@/components/HBox';
import { Link } from 'react-router-dom';
import { useResource } from '@/hooks';
import { UserView } from '@/components/UserView';
import { Do, Empty, ForEach } from '@/components/Syntax';
import { ReleaseCard } from '@/components/ReleaseCard';
import { Table } from '@/components/Table';
import { useManager } from '@/hooks';
import { PlayButton } from '@/components/PlayButton';
import LoadingPage from '@/pages/loading';
import ErrorPage from '@/pages/error';
import { FeatureCard } from '@/components/FeatureCard';
import { Megaphone } from '@phosphor-icons/react';

export function RecordingView({ recordingId }) {
  const manager = useManager()

  const { data: recording, isLoading, error, refetch } = useResource({
    queryKey: [manager?.type, manager?.slug, 'recording', recordingId],
    path: `/${manager?.type}/${manager?.slug}/recording/${recordingId}`,
    enabled: !!manager
  })

  const { data: releases, error: errorReleases } = useResource({
    queryKey: [manager?.type, manager?.slug, 'recording', recordingId, 'releases'],
    path: `/${manager?.type}/${manager?.slug}/recording/${recordingId}/releases`,
    enabled: !!manager
  })
  
  if (error) {
    return (
      <ErrorPage onTryAgainClick={() => refetch()} />
    ) 
  }

  if (isLoading || errorReleases) {
    return (
      <LoadingPage />
    )
  }

  if (recording) {
    return (
      <Container><br />
        {(recording.is_eligible_for_discovery_mode) && (
            <FeatureCard
              sx={{ marginBottom: 1, p: 1 }}
              as={Link}
              name={i18n.t('dm-eligibility.name')}
              description={i18n.t('dm-eligible.description')}              
              Icon={Megaphone}
              to={`/${manager?.type}/${manager?.slug}/marketing/discovery-mode`}
            />
          )}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <HBox>
            {recording.has_audio && (
              <PlayButton size={56} recording={recording} />
            )}
            <Typography level="h1">{recording.name}</Typography>
          </HBox>
          <Typography level="h2">{recording.primary_genre?.name}</Typography>
          {!(recording?.has_audio) && (
            <Typography>{i18n.t('recording-is-missing-audio')}</Typography>
          )}
          <Button
            aria-label="Edit recording"
            component={Link}
            to={`edit`}
            variant="outlined"
          >{i18n.t('edit')}</Button>
          <Typography level="h3">{i18n.t('contributors')}</Typography>
          <HBox sx={{ gap: 1 }}>
            <ForEach collection={recording.recording_contributors}>
              <Do>
                {contributor => (
                  <span key={contributor.id}>
                    <UserView
                      user={{ username: contributor.person.name, imageUrl: contributor.person.image_url }}
                      role={contributor?.roles?.map(r => r.name).join(', ')}
                    />
                  </span>
                )}
              </Do>
              <Empty>
                <Typography fontStyle="italic">{i18n.t('recording-has-no-contributors')}</Typography>
              </Empty>
            </ForEach>
          </HBox>
          <Typography level="h3">{i18n.t('artists')}</Typography>
          <HBox sx={{ gap: 1 }}>
            <ForEach collection={recording.recording_artists}>
              <Do>
                {recordingArtist => (
                  <UserView key={recordingArtist.id} user={{ username: recordingArtist.artist.name, image_url: recordingArtist.artist.image_url }} />
                )}
              </Do>
              <Empty>
                <i>{i18n.t('track-has-no-artists')}</i>
              </Empty>
            </ForEach>
          </HBox>
          <Typography level="h3">{i18n.t('royalty-splits')}</Typography>
          <Table style={{ width: '100%' }}>
            <ForEach collection={recording.recording_payees}>
              <Do>
                {recordingPayee => (
                  <tr key={recordingPayee.id}>
                    <td>
                      <UserView key={recordingPayee.id} user={{ username: recordingPayee.payee.name }} />
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      {recordingPayee.payee.email}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {recordingPayee.percentage} %
                    </td>
                  </tr>
                )}
              </Do>
              <Empty>
                <tr>
                  <td>{i18n.t('master-owner')}</td>
                  <td />
                  <td style={{ textAlign: 'right' }}>100 %</td>
                </tr>
              </Empty>
            </ForEach>
            <tr style={{ fontWeight: 'bold' }} className="tr-summary">
              <td>{i18n.t('total')}</td>
              <td />
              <td style={{ textAlign: 'right' }}>100 %</td>
            </tr>
          </Table>
          <Typography level="h3">{i18n.t('statistics')}</Typography>
          {recording.is_distributed && (
            <StatisticsContainer
              recordingIds={[recording.id]}
              showTopList={false}
            />
          )}
          <Typography level="h3">{i18n.t('in-releases')}</Typography>
          <Grid sx={{ alignSelf: 'stretch' }} container spacing={2}>
            <ForEach collection={releases?.results}>
              <Do>
                {(release, i) => (
                  <Grid key={release.id} md={3}> 
                    <ReleaseCard
                      sx={{ p: 0, transitionDelay: `${i * 100}ms`}}
                      component={Link}
                      to={`../../release/${release.id}`}
                      {...release}
                    />
                  </Grid>
                )}
              </Do>
              <Empty>
                <Typography fontSize="italic">{i18n.t('track-has-not-yet-been-added-to-any-releases')}</Typography>
              </Empty>
            </ForEach>
          </Grid>
        </Box>
      </Container>
    )
  }
}

RecordingView.propTypes = {
  recordingId: PropTypes.string
}

export default function RecordingPage() {
  const { recordingId } = useParams()

  return (
    <React.Suspense fallback={<Loading />}>
      <RecordingView recordingId={recordingId} />
    </React.Suspense>
  )
}